import { Injectable, inject } from '@angular/core';
import { Params, Router } from '@angular/router';
import { RouterScrollSuppressorService } from '@app/app/shared/services/router-scroll-suppressor.service';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  PageSizeOption,
} from '@app/app/shared/styleguide/components/pagination/pagination-footer.component';

export type TableUrlValue = string | number | boolean | null | undefined;

@Injectable({ providedIn: 'root' })
export class TableUrlStateService {
  private readonly router = inject(Router);
  private readonly scrollSuppressor = inject(RouterScrollSuppressorService);

  params(): Params {
    return this.router.parseUrl(this.router.url).queryParams;
  }

  string(key: string, fallback = ''): string {
    const value = this.params()[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  number(key: string, fallback: number): number {
    const value = Number(this.params()[key]);
    return Number.isInteger(value) && value >= 0 ? value : fallback;
  }

  pageIndex(key = 'page'): number {
    return Math.max(0, this.number(key, 1) - 1);
  }

  pageSize(key = 'size', options: readonly PageSizeOption[] = DEFAULT_PAGE_SIZE_OPTIONS): number {
    const value = this.number(key, DEFAULT_PAGE_SIZE);
    return options.some((option) => option.value === value) ? value : DEFAULT_PAGE_SIZE;
  }

  option<T extends string>(key: string, allowed: readonly T[], fallback: T | null = null): T | null {
    const value = this.params()[key];
    return allowed.includes(value as T) ? (value as T) : fallback;
  }

  patch(values: Record<string, TableUrlValue>): void {
    const tree = this.router.parseUrl(this.router.url);
    const next: Params = { ...tree.queryParams };
    let changed = false;

    for (const [key, value] of Object.entries(values)) {
      const normalized = value === null || value === undefined || value === '' ? undefined : String(value);
      const current = typeof next[key] === 'string' ? (next[key] as string) : undefined;
      if (current === normalized) {
        continue;
      }
      changed = true;
      if (normalized === undefined) {
        delete next[key];
      } else {
        next[key] = normalized;
      }
    }

    if (!changed) {
      return;
    }

    tree.queryParams = next;
    this.scrollSuppressor.suppressNextRouterScroll();
    this.router.navigateByUrl(tree, { replaceUrl: true });
  }
}
